import { useEffect, useState } from 'react';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { formatDateUTC } from '../utils/formatDate';

import { useNavigate, useParams } from 'react-router-dom';
import Button from '../Components/Buttons';
import {
  DocumentCollectionApiProvider,
  useDocumentCollectionApi,
} from '../Services/DocumentCollectionApiProvider';

function DocumentCollections({ canEdit }) {
  const { getDocumentCollections, deleteDocumentCollection } = useDocumentCollectionApi();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collections, setCollections] = useState([]);
  const [totalCollections, setTotalCollections] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: true },
    { name: 'Name', selector: (row) => row.name, sortable: true },
    { name: 'Status', selector: (row) => (row.is_active ? 'Active' : 'Inactive'), sortable: true },
    { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: true },
    { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: true },
  ];

  const fetchCollections = async (skip, limit) => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }
    try {
      setLoading(true);
      const response = await getDocumentCollections(skip, limit);
      setCollections(response.items);
      setTotalCollections(response.totalCount);
      setItemsPerPage(limit);
    } catch (error) {
      console.error('Error fetching document collections:', error);
      setError('Failed to load document collections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections(0, itemsPerPage);
  }, []);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/document-collections/${row.id}`);
  };

  const handleDelete = async (selectedId) => {
    if (!selectedId) return;
    showConfirmation(
      `Are you sure you want to delete the selected Document Collection?`,
      async () => {
        try {
          setLoading(true);
          await deleteDocumentCollection(selectedId);
          fetchCollections(0, itemsPerPage);
          showAlert('success', 'Document Collection was successfully deleted!');
        } catch (error) {
          showAlert('fail', 'Failed to delete the document collection!');
        }
      },
    );
  };

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="Document Collections"
                columns={columns}
                data={collections}
                selectableRowsHighlight
                selectableRows
                progressPending={loading}
                paginationPerPage={itemsPerPage}
                onRowClicked={handleRowClicked}
                onDelete={handleDelete}
                paginationTotalRows={totalCollections}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onChangePage={fetchCollections}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/document-collections/new-collection`)
                  }
                >
                  Create New Collection
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DocumentCollectionsWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <DocumentCollectionApiProvider>
        <DocumentCollections canEdit={canEdit} />
      </DocumentCollectionApiProvider>
    </ConfirmationProvider>
  );
}

export default DocumentCollectionsWrapper;
